import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";

import { EmailContentType } from "@simplyk/common";
import { useSearchParams } from "next/navigation";
import { useRouter } from "next/router";
import { UseFormReturn } from "react-hook-form";

import { trpc } from "../../../../common/helpers/trpc";
import { removeSearchParams, useUrlBuilder } from "../../../../common/hooks/useUrlBuilder";
import { ActiveOrganizationMember } from "../../../../common/types/trpc";
import { EmailContentLinkedFormType } from "../../EmailEditor/types";
import { EmailContentBuilderType } from "../../EmailEditor/useGetEmailContent";
import { EmailContentFormData } from "../types";

import { ExitAction, getExitBehavior } from "./getExitBehavior";
import { useSaveBody } from "./useSaveBody";
import { useSavePreferences } from "./useSavePreferences";

import { useAutoSave } from "@/components/FullScreenEmailEditorContainer/contexts/useAutoSave";
import { AuditErrors, EMPTY_AUDIT_ERRORS } from "@/components/FullScreenEmailEditorContainer/types";
import { useStepNavigation } from "@/components/LiveEditorElements/Navigation/useStepNavigation";
export type EmailSavingState = "error" | "saved" | "saving" | undefined;

export type FullScreenEmailEditorStep = "template" | "body" | "preferences" | "recipients" | "delivery";

export interface PendingExit {
  targetUrl: string;
}

const EMAIL_EDITOR_STEPS: FullScreenEmailEditorStep[] = ["template", "body", "preferences", "recipients", "delivery"];
const WORKFLOW_EMAIL_SKIPPED_STEPS: FullScreenEmailEditorStep[] = ["recipients", "delivery"];

export interface DeliverySettings {
  mode: "schedule" | "send";
  scheduledFor: EmailContentBuilderType["scheduledFor"];
  scheduleOffset: EmailContentBuilderType["scheduleOffset"];
}

interface FullScreenEmailEditorContextValue {
  isLoading: boolean;
  onExitUrl: string;
  exitEditor: (action: ExitAction, targetUrl: string) => Promise<void>;
  onBack: () => void;
  onNext: () => Promise<void>;
  save: () => Promise<boolean>;
  currentStep: FullScreenEmailEditorStep;
  stepKeys: FullScreenEmailEditorStep[];
  isLastStep: boolean;
  goToStep: (step: FullScreenEmailEditorStep) => void;
  form: UseFormReturn<EmailContentFormData>;
  savingState: EmailSavingState;
  lastSavedAt: Date;
  setSavingState: (state: EmailSavingState) => void;
  showSaveAndExit: boolean;
  refetchEmailContent: () => Promise<void>;
  prioritizeAuditTab: boolean;
  setPrioritizeAuditTab: (value: boolean) => void;
  auditErrors: AuditErrors;
  setAuditErrors: React.Dispatch<React.SetStateAction<AuditErrors>>;
  activeOrganizationMembers: ActiveOrganizationMember[] | undefined;
  activeOrganizationMembersLoading: boolean;
  emailContent: EmailContentBuilderType;
  linkedForm: EmailContentLinkedFormType;
  customDomainEmail: string | undefined;
  defaultFromZeffyContactEmail: string | undefined;
  recipientsValidationError: string | undefined;
  setRecipientsValidationError: (error: string | undefined) => void;
  firstDirty: boolean;
  isNewEmail: boolean;
  onEmailContentDeleted: () => void;
  pendingExit: PendingExit | null;
  isExitSaving: boolean;
  confirmExitWithSave: () => Promise<void>;
  discardExit: () => Promise<void>;
  dismissExitModal: () => void;
}

const FullScreenEmailEditorContext = createContext<FullScreenEmailEditorContextValue | null>(null);

interface ProviderProps {
  form: UseFormReturn<EmailContentFormData>;
  emailContent: EmailContentBuilderType;
  linkedForm: EmailContentLinkedFormType;
  onExitUrl: string;
  /** The default step to use when no hash is present in the URL */
  defaultStep: FullScreenEmailEditorStep;
  refetchEmailContent: () => Promise<void>;
  onEmailContentDeleted: () => void;
}

export const CREATION_FLOW_QUERY_PARAM = "isCreationFlow";
export const SEND_TO_MORE_RECIPIENTS_QUERY_PARAM = "sendToMoreRecipients";

export const FullScreenEmailEditorProvider: FC<PropsWithChildren<ProviderProps>> = ({
  children,
  form,
  emailContent,
  linkedForm,
  onExitUrl,
  defaultStep,
  refetchEmailContent,
  onEmailContentDeleted,
}) => {
  const isWorkflowStepEmail = emailContent.emailType === EmailContentType.CRMWORKFLOW;
  const steps = useMemo(
    () =>
      EMAIL_EDITOR_STEPS.map((key) => ({
        key,
        shouldBeSkip: isWorkflowStepEmail && WORKFLOW_EMAIL_SKIPPED_STEPS.includes(key),
      })),
    [isWorkflowStepEmail]
  );
  const isValidEmailEditorStepKey = useCallback(
    (key: unknown): key is FullScreenEmailEditorStep => steps.some((step) => !step.shouldBeSkip && step.key === key),
    [steps]
  );

  const searchParams = useSearchParams();
  const isCreationFlow = Boolean(searchParams.get(CREATION_FLOW_QUERY_PARAM));

  const {
    currentStepKey: currentStep,
    currentStepIndex,
    liveEditorStepsKeys,
    updateCurrentStep: goToStep,
    goToNextStep,
    goToPreviousStep,
  } = useStepNavigation<FullScreenEmailEditorStep>({
    steps,
    isValidStepKey: isValidEmailEditorStepKey,
    lastSavedDraftStep: defaultStep,
  });
  const isLastStep = currentStepIndex === liveEditorStepsKeys.length - 1;
  const openedPastTemplateStep = useRef(currentStep !== "template" && searchParams.get("skipTemplateStep") !== "true");

  const { mutateAsync: automaticDeleteDraftEmailContentMutation } = trpc.automaticDeleteDraftEmailContent.useMutation();
  const { mutateAsync: deleteDraftEmailContentMutation } = trpc.deleteDraftEmailContent.useMutation();
  const utils = trpc.useUtils();
  const { data: activeOrganizationMembers, isLoading: activeOrganizationMembersLoading } =
    trpc.getActiveOrganizationMembers.useQuery();

  const [isLoading, setIsLoading] = useState(false);
  const [savingState, setSavingState] = useState<EmailSavingState>("saved");
  const [lastSavedAt, setLastSavedAt] = useState(
    () => new Date(emailContent.lastEditedAtUtc ?? emailContent.updatedAtUtc)
  );
  const recordSave = useCallback(() => setLastSavedAt(new Date()), []);
  const [recipientsValidationError, setRecipientsValidationError] = useState<string | undefined>(undefined);

  const [firstDirty, setFirstDirty] = useState(false);
  if (form.formState.isDirty && !firstDirty) {
    setFirstDirty(true);
  }

  const isNewEmail = useRef(emailContent.version === 1);
  const buildUrl = useUrlBuilder();

  const [prioritizeAuditTab, setPrioritizeAuditTab] = useState(false);
  const [auditErrors, setAuditErrors] = useState<AuditErrors>(EMPTY_AUDIT_ERRORS);

  useEffect(() => {
    setPrioritizeAuditTab(false);
    setAuditErrors(EMPTY_AUDIT_ERRORS);
  }, [currentStep]);

  const router = useRouter();

  const isEmailContentDeletedRef = useRef(false);
  const handleEmailContentDeleted = useCallback(() => {
    isEmailContentDeletedRef.current = true;
    onEmailContentDeleted();
  }, [onEmailContentDeleted]);

  const { save: saveBody } = useSaveBody({
    form,
    emailContent,
    setIsLoading,
    refetchEmailContent,
    onEmailContentDeleted: handleEmailContentDeleted,
  });

  const { save: savePreferences } = useSavePreferences({
    form,
    emailContent,
    setIsLoading,
    refetchEmailContent,
    onEmailContentDeleted: handleEmailContentDeleted,
  });

  const rawSave = ["recipients", "preferences"].includes(currentStep) ? savePreferences : saveBody;

  const resetForm = useCallback(() => {
    form.reset(form.getValues(), {
      keepValues: true,
      keepDefaultValues: false,
      keepDirty: false,
      keepTouched: true,
      keepIsSubmitted: true,
      keepSubmitCount: true,
    });
  }, [form]);

  const save = useCallback(async () => {
    try {
      await rawSave();
    } catch {
      setSavingState("error");
      resetForm();
      return false;
    }
    resetForm();
    return true;
  }, [rawSave, resetForm]);

  const { isSubmitted } = form.formState;

  useAutoSave<EmailContentFormData>({
    form,
    rawSave,
    resetForm,
    savingState,
    setSavingState,
    onSaved: recordSave,
  });

  const deleteDraft = useCallback(
    async (emailContentId: string) => {
      await automaticDeleteDraftEmailContentMutation({ emailContentId });
      void utils.getCommunicationEmailStatus.invalidate(undefined, { refetchType: "none" });
    },
    [automaticDeleteDraftEmailContentMutation, utils.getCommunicationEmailStatus]
  );

  const manualDeleteDraft = useCallback(
    async (emailContentId: string) => {
      await deleteDraftEmailContentMutation({ emailContentId });
      void utils.getCommunicationEmailStatus.invalidate(undefined, { refetchType: "none" });
    },
    [deleteDraftEmailContentMutation, utils.getCommunicationEmailStatus]
  );

  const [pendingExit, setPendingExit] = useState<PendingExit | null>(null);
  const [isExitSaving, setIsExitSaving] = useState(false);

  const exitEditor = useCallback(
    async (action: ExitAction, targetUrl: string) => {
      const behavior = getExitBehavior(action, targetUrl, {
        firstDirty,
        isNewEmail: isNewEmail.current,
        currentStep,
        isCreationFlow,
        templateStepSkipped: openedPastTemplateStep.current,
      });

      if (behavior.shouldShowModal) {
        setPendingExit({ targetUrl });
        return;
      }

      if (behavior.shouldSave) {
        await save();
        if (isEmailContentDeletedRef.current) {
          return;
        }
      }
      if (behavior.shouldDeleteDraft) {
        await deleteDraft(emailContent.id);
      }
      setSavingState(undefined);

      if (behavior.navigateTo === "previous-step") {
        if (currentStep === "body") {
          const newUrl = buildUrl({
            searchParams: removeSearchParams("templateId"),
          });
          await router.replace(newUrl, undefined, { shallow: true });
        }
        goToPreviousStep();
      } else {
        void router.push(behavior.navigateTo);
      }
    },
    [
      firstDirty,
      isNewEmail,
      currentStep,
      isCreationFlow,
      save,
      deleteDraft,
      emailContent.id,
      setSavingState,
      goToPreviousStep,
      router,
      buildUrl,
    ]
  );

  const onBack = useCallback(() => {
    void exitEditor("back", onExitUrl);
  }, [exitEditor, onExitUrl]);

  const confirmExitWithSave = useCallback(async () => {
    if (!pendingExit) {
      return;
    }

    setIsExitSaving(true);
    await save();
    if (!isEmailContentDeletedRef.current) {
      void router.push(pendingExit.targetUrl);
    }

    setIsExitSaving(false);
    setPendingExit(null);
  }, [pendingExit, save, router]);

  const discardExit = useCallback(async () => {
    if (!pendingExit) {
      return;
    }

    await manualDeleteDraft(emailContent.id);
    setSavingState(undefined);
    void router.push(pendingExit.targetUrl);

    setPendingExit(null);
  }, [pendingExit, manualDeleteDraft, emailContent.id, setSavingState, router]);

  const dismissExitModal = useCallback(() => {
    setPendingExit(null);
  }, []);

  const onNext = useCallback(async () => {
    if (isLastStep) {
      await exitEditor("save-and-exit", onExitUrl);
      return;
    }
    const saved = await save();
    if (isEmailContentDeletedRef.current) {
      return;
    }
    if (saved) {
      setSavingState("saved");
      recordSave();
    }
    goToNextStep();
  }, [isLastStep, exitEditor, onExitUrl, save, recordSave, goToNextStep]);

  const handleNext = useMemo(() => form.handleSubmit(onNext), [form, onNext]);

  const { data: customDomainData } = trpc.fetchCustomDomain.useQuery({});

  const customDomainEmail =
    customDomainData?.customDomain?.status === "completed" ? customDomainData.customDomain.contactEmail : undefined;

  const defaultFromZeffyContactEmail = customDomainData?.defaultZeffyContactEmail;

  return (
    <FullScreenEmailEditorContext.Provider
      value={{
        onExitUrl,
        isLoading,
        currentStep,
        stepKeys: liveEditorStepsKeys,
        isLastStep,
        goToStep,
        onBack,
        exitEditor,
        onNext: handleNext,
        save,
        form,
        savingState,
        lastSavedAt,
        setSavingState,
        showSaveAndExit: isSubmitted,
        refetchEmailContent,
        prioritizeAuditTab,
        setPrioritizeAuditTab,
        auditErrors,
        setAuditErrors,
        activeOrganizationMembers,
        activeOrganizationMembersLoading,
        emailContent,
        linkedForm,
        customDomainEmail,
        defaultFromZeffyContactEmail,
        recipientsValidationError,
        setRecipientsValidationError,
        firstDirty,
        isNewEmail: isNewEmail.current,
        onEmailContentDeleted: handleEmailContentDeleted,
        pendingExit,
        isExitSaving,
        confirmExitWithSave,
        discardExit,
        dismissExitModal,
      }}
    >
      {children}
    </FullScreenEmailEditorContext.Provider>
  );
};

export const useFullScreenEmailEditorContext = () => {
  const context = useContext(FullScreenEmailEditorContext);
  if (!context) {
    throw new Error("useFullScreenEmailEditorContext must be used within FullScreenEmailEditorProvider");
  }
  return context;
};
